import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, 
  Edit, 
  Delete, 
  TickSquare, 
  CloseSquare, 
  ArrowUp, 
  ArrowDown,
  InfoCircle
} from 'react-iconly';
import { COLORS } from '../lib/styleGuide';
import { 
  getTaxonomyData, 
  updateTaxonomyValues, 
  removeTaxonomyValue,
  initializeTaxonomy,
  addTaxonomyValue
} from '../lib/taxonomyService';
import { SortableList } from '../components/ui';

// Style constants
const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};


export function AdminTaxonomy() {
  const navigate = useNavigate();
  const { currentUser, hasRole } = useAuth();
  
  // State for taxonomy data
  const [taxonomyData, setTaxonomyData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // State for editing
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [newValues, setNewValues] = useState({});
  const [editInputs, setEditInputs] = useState({});
  
  // State for validation
  const [validationErrors, setValidationErrors] = useState({});

  // Check if user is admin
  useEffect(() => {
    if (currentUser && !hasRole('admin')) {
      navigate('/');
      return;
    }
    
    if (currentUser && hasRole('admin')) {
      loadTaxonomyData();
    }
  }, [currentUser, hasRole, navigate]);

  // Load taxonomy data from database
  const loadTaxonomyData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Initialize taxonomy if it doesn't exist
      await initializeTaxonomy();
      
      // Get taxonomy data from database
      const data = await getTaxonomyData();
      setTaxonomyData(data);
      setNewValues(Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: '' }), {}));
      setEditInputs(Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: '' }), {}));
    } catch (error) {
      console.error('Error loading taxonomy data:', error);
      setError('Failed to load taxonomy data');
    } finally {
      setLoading(false);
    }
  };

  // Start editing a category
  const startEditing = (category) => {
    setEditingCategory(category);
    setEditingValues({ ...editingValues, [category]: null });
    setEditInputs({ ...editInputs, [category]: '' });
    setNewValues({ ...newValues, [category]: '' });
    setValidationErrors({});
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingCategory(null);
    setEditingValues({});
    setEditInputs({});
    setNewValues({});
    setValidationErrors({});
  };

  const handleAddValue = async (category) => {
    if (!newValues[category] || !newValues[category].trim()) return;
    
    const newValue = {
      value: newValues[category].toLowerCase().replace(/\s+/g, '_'),
      display: newValues[category].trim()
    };
    
    try {
      await addTaxonomyValue(category, newValue);
      setNewValues({ ...newValues, [category]: '' });
      loadTaxonomyData();
    } catch (error) {
      console.error('Error adding value:', error);
    }
  };

  const handleRemoveValue = async (category, valueObj) => {
    try {
      await removeTaxonomyValue(category, valueObj);
      loadTaxonomyData();
    } catch (error) {
      console.error('Error removing value:', error);
    }
  };

  const handleEditValue = (category, valueObj) => {
    setEditingValues({ ...editingValues, [category]: valueObj });
    setEditInputs({ ...editInputs, [category]: valueObj.display });
  };

  const handleSaveEdit = async (category) => {
    const editedValue = editingValues[category];
    const newDisplay = editInputs[category];
    
    if (!newDisplay || !newDisplay.trim()) return;
    
    const updatedValue = {
      ...editedValue,
      display: newDisplay.trim()
    };
    
    try {
      // Remove old value and add new one
      await removeTaxonomyValue(category, editedValue);
      await addTaxonomyValue(category, updatedValue);
      setEditingValues({ ...editingValues, [category]: null });
      setEditInputs({ ...editInputs, [category]: '' });
      loadTaxonomyData();
    } catch (error) {
      console.error('Error updating value:', error);
    }
  };

  const handleCancelEdit = (category) => {
    setEditingValues({ ...editingValues, [category]: null });
    setEditInputs({ ...editInputs, [category]: '' });
  };

  // @dnd-kit taxonomy value reordering
  const handleTaxonomyReorder = async (category, newOrder) => {
          // Reordering values for category
    
    try {
      await updateTaxonomyValues(category, newOrder);
      // Update local state immediately for better UX
      setTaxonomyData(prev => ({
        ...prev,
        [category]: { ...prev[category], values: newOrder }
      }));
    } catch (error) {
      console.error('Error reordering taxonomy values:', error);
      // Reload data to ensure consistency
      loadTaxonomyData();
    }
  };

  const handleMoveValue = async (category, valueObj, direction) => {
    const currentValues = taxonomyData[category]?.values || [];
    const currentIndex = currentValues.findIndex(v => v.value === valueObj.value);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= currentValues.length) return;
    
    const newValues = [...currentValues];
    [newValues[currentIndex], newValues[newIndex]] = [newValues[newIndex], newValues[currentIndex]];
    
    try {
      await updateTaxonomyValues(category, newValues);
      loadTaxonomyData();
    } catch (error) {
      console.error('Error reordering values:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#F9F9F9', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <div style={{ fontSize: 18, color: '#666' }}>Loading taxonomy data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#F9F9F9', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#F44336', marginBottom: 16 }}>{error}</div>
          <button 
            onClick={loadTaxonomyData}
            style={{
              background: COLORS.PRIMARY_GRADIENT,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#F9F9F9', 
      padding: '20px' 
    }}>
      {/* Header */}
      <div style={{ 
        background: 'white', 
        borderRadius: 12, 
        padding: '24px', 
        marginBottom: 24,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ 
              ...headingFont, 
              fontSize: 24, 
              color: '#222', 
              margin: '0 0 8px 0' 
            }}>
              Taxonomy Management
            </h1>
            <p style={{ 
              fontSize: 16, 
              color: '#666', 
              margin: 0,
              ...bodyFont
            }}>
              Manage product taxonomy and attributes for consistent categorization
            </p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: '#F5F5F5',
              color: '#666',
              border: '1px solid #E0E0E0',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Back to Admin
          </button>
        </div>
      </div>

      {/* Taxonomy Categories */}
      <div style={{ display: 'grid', gap: 24 }}>
        {taxonomyData && Object.keys(taxonomyData).length > 0 && (
          Object.entries(taxonomyData).map(([category, data]) => (
            <div key={category} className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{data.name}</h3>
                  <p className="text-sm text-gray-600">{data.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      data.required 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {data.required ? 'Required' : 'Optional'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      data.singleValue 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {data.singleValue ? 'Single Value' : 'Multi-Value'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {data.values && data.values.map((valueObj, index) => (
                  <div key={valueObj.value} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleMoveValue(category, valueObj, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          onClick={() => handleMoveValue(category, valueObj, 'down')}
                          disabled={index === data.values.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        >
                          <ArrowDown size={16} />
                        </button>
                      </div>
                      
                      {editingValues[category]?.value === valueObj.value ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editInputs[category]}
                            onChange={(e) => setEditInputs({ ...editInputs, [category]: e.target.value })}
                            className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                            placeholder="Display name"
                          />
                          <button
                            onClick={() => handleSaveEdit(category)}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <TickSquare size={16} />
                          </button>
                          <button
                            onClick={() => handleCancelEdit(category)}
                            className="p-1 text-gray-600 hover:text-gray-800"
                          >
                            <CloseSquare size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 font-mono">{valueObj.value}</span>
                          <span className="text-sm text-gray-900">→</span>
                          <span className="text-sm font-medium text-gray-900">{valueObj.display}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditValue(category, valueObj)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleRemoveValue(category, valueObj)}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <Delete size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-2">
                <input
                  type="text"
                  value={newValues[category] || ''}
                  onChange={(e) => setNewValues({ ...newValues, [category]: e.target.value })}
                  placeholder="Add new display name..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <button
                  onClick={() => handleAddValue(category)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  <Plus size={16} className="inline mr-1" />
                  Add
                </button>
              </div>
            </div>
          ))
        )}

        {taxonomyData && Object.keys(taxonomyData).length === 0 && (
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '24px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{
              fontSize: 16,
              color: '#666',
              margin: 0,
              ...bodyFont
            }}>
              No taxonomy data available. Please check your database connection.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
